'use client';

import { Box } from '@wishket/design-system';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import NewsletterSubscriptionFAB from '../NewsletterSubscriptionFAB';

import { ScrollTopFAB } from '@/shared/Components';
import {
  useAuth,
  useDeviceCheck,
  useLocalStorageState,
  useScrollDirection,
} from '@/shared/hooks';
import { STORAGE_KEY } from '@/shared/constants';

interface IntersectionState {
  isStartPassed: boolean;
  isEndVisible: boolean;
  isArticleVisible: boolean;
}

const ARTICLE_DETAIL_SERVER_ID = '#article-detail-server';
const ARTICLE_DETAIL_WRAPPER_ID = '#article-detail-wrapper';
const ARTICLE_DETAIL_START_ID = '#article-detail-start';
const ARTICLE_DETAIL_END_ID = '#article-detail-end';
const ARTICLE_DETAIL_MINIMUM_HEIGHT = 1000;

const ArticleContentFABBox = () => {
  const { isAuth } = useAuth();
  const { isDesktop } = useDeviceCheck();
  const [isArticleMounted, setIsArticleMounted] = useState(false);
  const [isFABBoxVisible, setIsFABBoxVisible] = useState<boolean>(false);
  const { isScrollDown } = useScrollDirection();
  const articleRef = useRef<Element | null>(null);
  const startRef = useRef<Element | null>(null);
  const endRef = useRef<Element | null>(null);
  const [subscriptionDate] = useLocalStorageState<Date>(
    STORAGE_KEY.SUBSCRIPTION
  );
  const [tutorialVisitDate] = useLocalStorageState<Date>(STORAGE_KEY.TUTORIAL);

  const hasTutorialEnded = !!tutorialVisitDate;
  const hasSubscribed = !!subscriptionDate;
  const [needSubscriptionFAB, setNeedSubscriptionFAB] = useState(false);

  useEffect(() => {
    setNeedSubscriptionFAB(!isAuth && hasTutorialEnded && !hasSubscribed);
  }, [isAuth, hasTutorialEnded, hasSubscribed]);

  useEffect(() => {
    const article = document.querySelector(ARTICLE_DETAIL_SERVER_ID);
    if (!article) return;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect.height > ARTICLE_DETAIL_MINIMUM_HEIGHT) {
          setIsArticleMounted(true);
          observer.disconnect();
        }
      });
    });

    observer.observe(article);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const needObserver = isDesktop || needSubscriptionFAB;

    if (needObserver && isArticleMounted) {
      const elements = {
        article: document.querySelector(ARTICLE_DETAIL_WRAPPER_ID),
        start: document.querySelector(ARTICLE_DETAIL_START_ID),
        end: document.querySelector(ARTICLE_DETAIL_END_ID),
      };

      if (!Object.values(elements).every(Boolean)) return;

      articleRef.current = elements.article;
      startRef.current = elements.start;
      endRef.current = elements.end;

      const state: IntersectionState = {
        isStartPassed: false,
        isEndVisible: false,
        isArticleVisible: false,
      };

      const updateFABVisibility = () => {
        const shouldShowFAB =
          state.isStartPassed && !state.isEndVisible && state.isArticleVisible;

        setIsFABBoxVisible(shouldShowFAB);
      };
      const options = { threshold: 0, rootMargin: '0px' };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const { target } = entry;
          switch (target) {
            case articleRef.current:
              state.isArticleVisible = entry.isIntersecting;
              break;
            case startRef.current:
              state.isStartPassed = !entry.isIntersecting;
              break;
            case endRef.current:
              state.isEndVisible = entry.isIntersecting;
              break;
          }
        });
        updateFABVisibility();
      }, options);

      [articleRef, startRef, endRef].forEach((ref) => {
        if (ref.current) observer.observe(ref.current);
      });

      return () => observer.disconnect();
    }
  }, [isDesktop, needSubscriptionFAB, isArticleMounted]);

  return (
    <aside
      data-testid="article-content-fab-box"
      className={twMerge(
        'fixed right-8 z-30 box-border flex w-fit gap-4 duration-300 ease-in-out desktop:bottom-8 desktop:duration-1000',
        isFABBoxVisible ? 'translate-y-0' : 'translate-y-container',
        isScrollDown ? `bottom-[83px]` : 'bottom-6'
      )}
    >
      <Box className="hidden desktop:block">
        <ScrollTopFAB />
      </Box>
      {needSubscriptionFAB && (
        <NewsletterSubscriptionFAB isVisible={isFABBoxVisible} />
      )}
    </aside>
  );
};

export default ArticleContentFABBox;
